function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }
function _classPrivateFieldInitSpec(obj, privateMap, value) { _checkPrivateRedeclaration(obj, privateMap); privateMap.set(obj, value); }
function _checkPrivateRedeclaration(obj, privateCollection) { if (privateCollection.has(obj)) { throw new TypeError("Cannot initialize the same private elements twice on an object"); } }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _classPrivateFieldGet(receiver, privateMap) { var descriptor = _classExtractFieldDescriptor(receiver, privateMap, "get"); return _classApplyDescriptorGet(receiver, descriptor); }
function _classExtractFieldDescriptor(receiver, privateMap, action) { if (!privateMap.has(receiver)) { throw new TypeError("attempted to " + action + " private field on non-instance"); } return privateMap.get(receiver); }
function _classApplyDescriptorGet(receiver, descriptor) { if (descriptor.get) { return descriptor.get.call(receiver); } return descriptor.value; }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'transform-class-properties is enabled and runs after the decorators transform.'); }
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { dismissModal, openModal } from '@market/ember';
import { guidFor } from '@ember/object/internals';
import { hbs } from 'ember-cli-htmlbars';
import { setComponentTemplate } from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import assert, { assertBoolean } from '@banking/ui/utils/assert';

//
// NOTE(steckel): The {{#if}} block here is not actually responsible for
// conditionally displaying the modal.
//
// Many implementations of <market-modal-partial> (and <market-modal-full>)
// look like this:
//
// ```
// {{#if showModal}}
//   <template {{open-modal}}>
//     <market-modal-partial></market-modal-partial>
//   </template>
// {{/if}}
// ```
//
// This can be deceiving, because it can appear that the if block is
// responsible for showing and hiding the modal, where that is not actually the
// case.
//
// The {{open-modal}} calls @market/ember's openModal function on the
// <template> tag which moves the template element's child <market-modal-partial>
// element into another position on the DOM under market's modal stack
// manager's control.
//
// To dismiss the modal, implementors must pass the copied <market-modal-partial>
// element to @market/ember's dismissModal function.
//
// Dismissing the modal deletes it from the DOM, resulting in an empty
// <template> element and preventing the modal from being displayed again.
//
// Where the example implementation solves for this (either on purpose or by
// accident) is by wrapping the markup in an if block and calling
// {{open-modal}} unconditionally, so that the condition's truthy evaluation
// coincides with its display **and gets re-rendered once the truthy value
// switches back between falsey and truthy.**
//
// The implementation below seeks to hide these implementation details and
// solve for the asymmetric API where displaying a modal is done in markup and
// dismissal is done in JavaScript. The `active` argument manages the display
// and dismissal without to better follow the "actions up, data down" pattern.
//

// Fixed setComponentTemplate that should already be exposed in TypeScript

export let TagName = /*#__PURE__*/function (TagName) {
  TagName["MarketModalFull"] = "market-modal-full";
  TagName["MarketModalPartial"] = "market-modal-partial";
  return TagName;
}({});
const template = hbs`
<template
    {{did-insert this.didInsert}}
    {{did-update this.onActiveDidUpdate this.active}}
    {{did-update this.onActiveArgDidUpdate @active}}>
  {{#if this.active}}
    {{#let (element this.tagName) as |Tag|}}
      <Tag
          {{on "marketDialogDismissed" this.marketDialogDismissed}}
          {{on "marketDialogLoaded" (or @onLoad (noop))}}
          class={{this.modalClassName}}
          data-market-features="typography"
          ...attributes>
        {{yield}}
      </Tag>
    {{/let}}
  {{/if}}
</template>
`;
export default (tagName => {
  var _class, _descriptor, _descriptor2, _guid;
  let Modal = (_class = (_guid = /*#__PURE__*/new WeakMap(), class Modal extends Component {
    constructor(owner, args) {
      super(owner, args);
      _initializerDefineProperty(this, "sentry", _descriptor, this);
      _defineProperty(this, "tagName", tagName);
      _classPrivateFieldInitSpec(this, _guid, {
        writable: true,
        value: guidFor(this)
      });
      _initializerDefineProperty(this, "_active", _descriptor2, this);
      this._active = args.active ?? false;
    }
    _getModalElement() {
      return document.querySelector(`${this.tagName}.${this.modalClassName}`);
    }
    get active() {
      return this._active;
    }
    set active(flag) {
      if (flag === this._active) {
        return;
      }
      this._active = flag;
      this.args.onActiveChange?.(this._active);
    }
    get modalClassName() {
      return `${_classPrivateFieldGet(this, _guid)}-modal`;
    }
    didInsert(element) {
      if (this.active) {
        openModal(element);
      }
    }
    async onActiveDidUpdate(element) {
      if (this.active) {
        openModal(element);
      } else {
        const modelElement = this._getModalElement();
        // TODO(steckel): figure out how you'd recover from this.
        try {
          if (modelElement) {
            // TODO(steckel): find a way to assert HTMLMarketModalPartialElement
            await dismissModal(modelElement);
          }
        } finally {
          this.args.onDismiss?.();
        }
      }
    }
    marketDialogDismissed() {
      this.active = false;
    }
    onActiveArgDidUpdate(_, args) {
      assert(args.length > 0);
      this.active = assertBoolean(args[0]);
    }

    /**
     * This cleans up the modal if it is still opened when the component destroys itself.
     * This is needed because the modal element is added into the dom via the market-context-manager.
     * If the component is destroyed but the modal is opened, modal element itself still lives in the
     * dom even though the Ember component has unmounted.
     */
    willDestroy() {
      super.willDestroy();
      const modelElement = this._getModalElement();
      if (modelElement) {
        try {
          // TODO(steckel): find a way to assert HTMLMarketModalPartialElement
          dismissModal(modelElement);
        } catch (e) {
          this.sentry.captureException(e);
        }
      }
    }
  }), (_descriptor = _applyDecoratedDescriptor(_class.prototype, "sentry", [service], {
    configurable: true,
    enumerable: true,
    writable: true,
    initializer: null
  }), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "_active", [tracked], {
    configurable: true,
    enumerable: true,
    writable: true,
    initializer: null
  }), _applyDecoratedDescriptor(_class.prototype, "didInsert", [action], Object.getOwnPropertyDescriptor(_class.prototype, "didInsert"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onActiveDidUpdate", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onActiveDidUpdate"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "marketDialogDismissed", [action], Object.getOwnPropertyDescriptor(_class.prototype, "marketDialogDismissed"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onActiveArgDidUpdate", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onActiveArgDidUpdate"), _class.prototype)), _class);
  setComponentTemplate(template, Modal);
  return Modal;
});